import React, { useState, useEffect, useCallback, useRef } from 'react';
import { openPdf } from '../utils/pdf';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export type BeoordeelActie = {
  key: string;
  label: string;
  kleur: 'green' | 'red' | 'amber';
  redenen?: string[];                 // indien gezet: redenkeuze + verplicht
  template_key: string;               // welke template het bericht levert
  context: (reden: string, toelichting: string) => Record<string, any>;  // variabelen voor de template
  bijlage?: string;                   // label van de PDF-bijlage (optioneel)
};

const KLEUR: Record<string, string> = {
  green: 'bg-emerald-600 hover:bg-emerald-700',
  red: 'bg-red-600 hover:bg-red-700',
  amber: 'bg-amber-500 hover:bg-amber-600',
};

/** Vervang {variabele} door de waarde uit de context; onbekende blijven staan. */
const vul = (t: string, ctx: Record<string, any>) =>
  (t || '').replace(/\{(\w+)\}/g, (m, k) => (ctx[k] != null && ctx[k] !== '' ? String(ctx[k]) : m))
    .replace(/\n{3,}/g, '\n\n').trim();

export const BeoordelingModal: React.FC<{
  titel: string;
  context: string;
  acties: BeoordeelActie[];
  token: string;
  invoiceId?: string;                 // indien gezet: bijlagen kunnen worden toegevoegd
  uitvoeren: (actieKey: string, reden: string, toelichting: string, onderwerp: string, bericht: string) => Promise<{ bericht_id?: string } | null>;
  onClose: () => void;
  onDone: () => void;
}> = ({ titel, context, acties, token, invoiceId, uitvoeren, onClose, onDone }) => {
  const [actieKey, setActieKey] = useState(acties[0]?.key || '');
  const [reden, setReden] = useState('');
  const [toelichting, setToelichting] = useState('');
  const [busy, setBusy] = useState(false);
  const [klaar, setKlaar] = useState<{ bericht_id?: string } | null>(null);
  const [bijlagen, setBijlagen] = useState<any[]>([]);
  const [uploaden, setUploaden] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Template-tekst (met {variabelen}) + het bewerkbare, ingevulde bericht
  const [raw, setRaw] = useState<{ onderwerp: string; inhoud: string } | null>(null);
  const [onderwerp, setOnderwerp] = useState('');
  const [body, setBody] = useState('');
  const [bewerkt, setBewerkt] = useState(false);   // handmatig aangepast → niet meer auto-invullen

  const actie = acties.find((a) => a.key === actieKey) || acties[0];
  const heeftReden = !!actie?.redenen?.length;
  const gekozenReden = reden || (actie?.redenen?.[0] || '');

  const laadBijlagen = useCallback(() => {
    if (!invoiceId) return;
    fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/bijlagen`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : [])).then(setBijlagen).catch(() => setBijlagen([]));
  }, [invoiceId, token]);
  useEffect(() => { laadBijlagen(); }, [laadBijlagen]);

  // Template ophalen zodra de gekozen actie (dus template_key) wijzigt
  useEffect(() => {
    if (!actie?.template_key) return;
    setBewerkt(false);
    fetch(`${API_BASE_URL}/api/admin/templates/detail/${actie.template_key}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((t) => setRaw(t ? { onderwerp: t.onderwerp || '', inhoud: t.inhoud || '' } : { onderwerp: '', inhoud: '' }))
      .catch(() => setRaw({ onderwerp: '', inhoud: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actie?.template_key, token]);

  // Ingevuld voorbeeld (her)berekenen zolang de tekst niet handmatig is aangepast
  useEffect(() => {
    if (!raw || bewerkt) return;
    const ctx = actie ? actie.context(gekozenReden, toelichting) : {};
    setOnderwerp(vul(raw.onderwerp, ctx));
    setBody(vul(raw.inhoud, ctx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw, gekozenReden, toelichting, actieKey]);

  const herstelUitTemplate = () => {
    if (!raw || !actie) return;
    const ctx = actie.context(gekozenReden, toelichting);
    setOnderwerp(vul(raw.onderwerp, ctx));
    setBody(vul(raw.inhoud, ctx));
    setBewerkt(false);
  };

  const uploadBijlage = async (files: FileList | null) => {
    if (!invoiceId || !files || files.length === 0) return;
    setUploaden(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', file);
        await fetch(`${API_BASE_URL}/api/admin/invoices/${invoiceId}/beoordeling-bijlage`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        });
      }
      if (fileRef.current) fileRef.current.value = '';
      laadBijlagen();
    } finally { setUploaden(false); }
  };

  const verwijderBijlage = async (id: string) => {
    await fetch(`${API_BASE_URL}/api/admin/bijlagen/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    laadBijlagen();
  };

  const submit = async () => {
    setBusy(true);
    try {
      // Alleen handmatig aangepaste tekst overschrijft de template; anders rendert de backend zelf
      const r = await uitvoeren(actie.key, gekozenReden, toelichting, bewerkt ? onderwerp : '', bewerkt ? body : '');
      setKlaar(r || {});
      onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[92vh] overflow-y-auto rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h2 className="text-lg font-semibold text-mfb-blue">{titel}</h2>
            <p className="text-xs text-slate-400">{context}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        {klaar ? (
          <div className="mt-4 space-y-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Beoordeling vastgelegd. Het bericht is naar het lid verzonden (en als e-mail zodra die is ingesteld).
            </div>
            <div className="flex gap-2">
              {klaar.bericht_id && (
                <button onClick={() => openPdf(`/api/admin/berichten/${klaar.bericht_id}/bijlage`, token)}
                  className="rounded-lg bg-mfb-blue px-4 py-2 text-sm font-semibold text-white hover:bg-mfb-blue-dark">📄 Open document (PDF)</button>
              )}
              <button onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Sluiten</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {/* Beslissing */}
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Beslissing</label>
              <div className="flex flex-wrap gap-2">
                {acties.map((a) => (
                  <button key={a.key} onClick={() => { setActieKey(a.key); setReden(''); }}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium ${actieKey === a.key ? 'border-mfb-blue ring-2 ring-mfb-blue bg-mfb-blue/5 text-mfb-blue' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            {heeftReden && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reden</label>
                <select value={gekozenReden} onChange={(e) => setReden(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white">
                  {actie.redenen!.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Toelichting (handmatig){heeftReden ? '' : ' — optioneel'}</label>
              <textarea value={toelichting} onChange={(e) => setToelichting(e.target.value)} rows={3}
                placeholder="Onderbouwing voor het lid; komt in het voorbeeldbericht (via {toelichting}) én in het PDF-document…"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
            </div>

            {/* Bijlagen bij de beoordeling */}
            {invoiceId && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Bijlagen bij deze beoordeling</label>
                <div className="rounded-xl border border-dashed border-slate-300 p-3">
                  {bijlagen.length > 0 && (
                    <ul className="mb-2 space-y-1">
                      {bijlagen.map((b) => (
                        <li key={b.id} className="flex items-center justify-between text-sm">
                          <button type="button" onClick={() => openPdf(`/api/admin/bijlagen/${b.id}`, token)} className="text-mfb-blue hover:underline truncate">
                            📎 {b.bestandsnaam}{b.soort === 'beoordeling' ? '' : ` (${b.soort})`}
                          </button>
                          <button type="button" onClick={() => verwijderBijlage(b.id)} className="ml-2 text-xs text-slate-400 hover:text-red-600">verwijderen</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <input ref={fileRef} type="file" multiple onChange={(e) => uploadBijlage(e.target.files)} className="hidden" id="beoordeling-bijlage" />
                  <label htmlFor="beoordeling-bijlage" className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50">
                    {uploaden ? 'Uploaden…' : '+ Bestand toevoegen'}
                  </label>
                  <span className="ml-2 text-xs text-slate-400">Zichtbaar voor het lid bij deze factuur · max 10 MB per bestand</span>
                </div>
              </div>
            )}

            {/* Bewerkbaar bericht uit de template */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bericht / e-mail aan het lid (uit template — aanpasbaar)</div>
                <button type="button" onClick={herstelUitTemplate} className="text-xs font-medium text-mfb-blue hover:underline">↺ Uit template</button>
              </div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Onderwerp</label>
              <input value={onderwerp} onChange={(e) => { setOnderwerp(e.target.value); setBewerkt(true); }}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-2" />
              <label className="block text-xs font-medium text-slate-600 mb-1">Bericht</label>
              <textarea value={body} onChange={(e) => { setBody(e.target.value); setBewerkt(true); }} rows={8}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
              {actie?.bijlage && <div className="mt-2 text-xs text-mfb-orange">📎 Bijlage: {actie.bijlage}</div>}
              {bewerkt && <div className="mt-1 text-[11px] text-slate-400">Handmatig aangepast — deze tekst wordt zo verstuurd (klik “Uit template” om te herstellen).</div>}
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100">Annuleren</button>
              <button onClick={submit} disabled={busy} className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${KLEUR[actie?.kleur] || KLEUR.green}`}>
                {busy ? 'Bezig…' : `${actie?.label} & bericht versturen`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BeoordelingModal;
